import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import { Button } from '../components/ui/button';
import { 
  LayoutDashboard, 
  Package, 
  FolderOpen, 
  MapPin, 
  Users, 
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  Monitor,
  History,
  Settings,
  ScanLine,
  Theater,
  Box,
  Shirt
} from 'lucide-react';
import { cn } from '../lib/utils';

const Sidebar = () => {
  const { user, logout, isAdmin, isViewer } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { settings } = useSettings();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const navItems = [
    { 
      path: '/', 
      icon: LayoutDashboard, 
      label: 'Dashboard',
      show: !isViewer(),
      section: 'main'
    },
    { 
      path: '/items', 
      icon: Package, 
      label: 'Items',
      show: true,
      section: 'av'
    },
    { 
      path: '/scanner', 
      icon: ScanLine, 
      label: 'QR Scanner',
      show: true,
      section: 'main'
    },
    { 
      path: '/categories', 
      icon: FolderOpen, 
      label: 'Categories',
      show: !isViewer(),
      section: 'av'
    },
    { 
      path: '/locations', 
      icon: MapPin, 
      label: 'Storage Locations',
      show: !isViewer(),
      section: 'av'
    },
    { 
      path: '/history', 
      icon: History, 
      label: 'Check In/Out History',
      show: !isViewer(),
      section: 'av'
    },
    // Props section
    { 
      path: '/props', 
      icon: Theater, 
      label: 'Props',
      show: true,
      section: 'props'
    },
    { 
      path: '/containers', 
      icon: Box, 
      label: 'Containers',
      show: true,
      section: 'props'
    },
    { 
      path: '/prop-categories', 
      icon: FolderOpen, 
      label: 'Prop Categories',
      show: !isViewer(),
      section: 'props'
    },
    // Costumes section
    { 
      path: '/costumes', 
      icon: Shirt, 
      label: 'Costumes',
      show: true,
      section: 'costumes'
    },
    { 
      path: '/wardrobes', 
      icon: Box, 
      label: 'Wardrobes',
      show: true,
      section: 'costumes'
    },
    { 
      path: '/costume-categories', 
      icon: FolderOpen, 
      label: 'Costume Categories',
      show: !isViewer(),
      section: 'costumes'
    },
    { 
      path: '/users', 
      icon: Users, 
      label: 'Users',
      show: isAdmin(),
      section: 'admin'
    },
    { 
      path: '/settings', 
      icon: Settings, 
      label: 'Settings',
      show: isAdmin(),
      section: 'admin'
    },
  ];

  const filteredNavItems = navItems.filter(item => item.show);

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        data-testid="mobile-menu-btn"
        onClick={() => setIsOpen(!isOpen)}
        className="fixed top-4 left-4 z-50 p-2 rounded-lg bg-card border border-border md:hidden"
      >
        {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed left-0 top-0 h-screen w-64 bg-slate-900 text-white z-50 transition-transform duration-300",
        "md:translate-x-0",
        isOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-700">
            <div className="flex items-center gap-3">
              {settings?.logo_url ? (
                <img 
                  src={settings.logo_url} 
                  alt="School Logo" 
                  className="w-10 h-10 rounded-xl object-contain bg-white p-1"
                />
              ) : (
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: settings?.accent_color || '#2563EB' }}
                >
                  <Monitor className="w-5 h-5 text-white" strokeWidth={1.5} />
                </div>
              )}
              <div>
                <h1 className="font-bold text-lg tracking-tight">{settings?.school_name?.split(' ')[0] || 'SchoolAV'}</h1>
                <p className="text-xs text-slate-400">Inventory System</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {/* Main section */}
            {filteredNavItems.filter(i => i.section === 'main').map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200",
                    isActive ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  )}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
            
            {/* AV Equipment Section */}
            {filteredNavItems.filter(i => i.section === 'av').length > 0 && (
              <div className="pt-4">
                <p className="px-4 py-1 text-xs font-semibold text-slate-500 uppercase tracking-wider">AV Equipment</p>
              </div>
            )}
            {filteredNavItems.filter(i => i.section === 'av').map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200",
                    isActive ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  )}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
            
            {/* Props Section */}
            {filteredNavItems.filter(i => i.section === 'props').length > 0 && (
              <div className="pt-4">
                <p className="px-4 py-1 text-xs font-semibold text-slate-500 uppercase tracking-wider">Theatre Props</p>
              </div>
            )}
            {filteredNavItems.filter(i => i.section === 'props').map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200",
                    isActive ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  )}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
            
            {/* Costumes Section */}
            {filteredNavItems.filter(i => i.section === 'costumes').length > 0 && (
              <div className="pt-4">
                <p className="px-4 py-1 text-xs font-semibold text-slate-500 uppercase tracking-wider">Costumes</p>
              </div>
            )}
            {filteredNavItems.filter(i => i.section === 'costumes').map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200",
                    isActive ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  )}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
            
            {/* Admin Section */}
            {filteredNavItems.filter(i => i.section === 'admin').length > 0 && (
              <div className="pt-4">
                <p className="px-4 py-1 text-xs font-semibold text-slate-500 uppercase tracking-wider">Admin</p>
              </div>
            )}
            {filteredNavItems.filter(i => i.section === 'admin').map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200",
                    isActive ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  )}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User & Theme */}
          <div className="p-4 border-t border-slate-700 space-y-4">
            {/* Theme Toggle */}
            <button
              data-testid="theme-toggle-btn"
              onClick={toggleTheme}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-slate-300 hover:bg-slate-800 transition-colors"
            >
              <span className="font-medium">Theme</span>
              {theme === 'dark' ? (
                <Moon className="w-5 h-5" />
              ) : (
                <Sun className="w-5 h-5" />
              )}
            </button>

            {/* User Info */}
            <div className="px-4 py-3 rounded-lg bg-slate-800">
              <p className="font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              <span className="inline-block mt-2 px-2 py-0.5 text-xs font-medium bg-blue-600/20 text-blue-400 rounded">
                {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
              </span>
            </div>

            {/* Logout */}
            <Button
              data-testid="logout-btn"
              variant="ghost"
              onClick={logout}
              className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
